// ABC SEO/GEO — Site Audit. LIVE: real crawl + on-page checks scored by the API.
(function () {
  const { Icon } = window.SGIcons;
  const { Card, CardHead, Gauge, PageHead, ActionBtn } = window.SGKit;

  /* health hero + counts (from the live audit summary) */
  function HealthHero({ score, summary }) {
    const s = summary || { passed: 0, warnings: 0, failed: 0, total: 0 };
    const counts = [
      { label: 'Errors', value: s.failed, color: 'var(--sg-red)', bg: 'var(--sg-red-50)', icon: 'x' },
      { label: 'Warnings', value: s.warnings, color: 'var(--sg-amber)', bg: 'var(--sg-amber-50)', icon: 'info' },
      { label: 'Passed', value: s.passed, color: 'var(--sg-green)', bg: 'var(--sg-green-50)', icon: 'check' },
      { label: 'Checks run', value: s.total, color: 'var(--sg-text-2)', bg: 'var(--sg-sunken)', icon: 'eye' },
    ];
    const blurb = score >= 80 ? 'Healthy overall — clear the remaining errors for the biggest lift.'
      : score >= 50 ? 'Solid foundation with real issues to fix. Start at the top of the list.'
      : 'Several technical issues are holding you back — the fixes below are prioritized.';
    return (
      <Card style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap', animation: 'sg-rise 0.4s var(--sg-ease) both' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flex: '1 1 300px' }}>
          <Gauge value={score} color="var(--sg-blue-500)" size={148} suffix="%" />
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 18, color: 'var(--sg-text)' }}>Site Health</span>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '2px 8px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--sg-text-2)', maxWidth: 230, lineHeight: 1.5 }}>{blurb}</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, flex: '1 1 320px' }}>
          {counts.map((c) => (
            <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)' }}>
              <span style={{ width: 36, height: 36, borderRadius: 10, background: c.bg, color: c.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Icon name={c.icon} size={18} strokeWidth={2.4} /></span>
              <div>
                <div className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 22, color: c.color, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 12, color: 'var(--sg-text-2)', marginTop: 3 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  }

  /* core web vitals — real when a PageSpeed key is configured, else an upsell note */
  function CoreVitals({ ps }) {
    if (!ps) {
      return (
        <Card style={{ padding: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{ width: 40, height: 40, flex: 'none', borderRadius: 11, background: 'var(--sg-blue-50)', color: 'var(--sg-blue-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="activity" size={19} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--sg-text)' }}>Core Web Vitals</div>
            <div style={{ fontSize: 13, color: 'var(--sg-text-2)', marginTop: 2 }}>Add a free Google PageSpeed key to include real-world load speed (LCP/INP/CLS) in this audit.</div>
          </div>
        </Card>
      );
    }
    const grade = (ok, warn) => (v) => v == null ? { s: '—', c: 'var(--sg-text-3)', bg: 'var(--sg-sunken)' }
      : v <= ok ? { s: 'Good', c: 'var(--sg-green)', bg: 'var(--sg-green-50)' }
      : v <= warn ? { s: 'Needs work', c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)' }
      : { s: 'Poor', c: 'var(--sg-red)', bg: 'var(--sg-red-50)' };
    const rows = [
      { k: 'LCP', name: 'Largest Contentful Paint', v: ps.lcp != null ? (ps.lcp / 1000).toFixed(1) + 's' : '—', g: grade(2500, 4000)(ps.lcp) },
      { k: 'TBT', name: 'Total Blocking Time', v: ps.tbt != null ? Math.round(ps.tbt) + 'ms' : '—', g: grade(200, 600)(ps.tbt) },
      { k: 'CLS', name: 'Cumulative Layout Shift', v: ps.cls != null ? ps.cls.toFixed(2) : '—', g: grade(0.1, 0.25)(ps.cls) },
    ];
    return (
      <Card>
        <CardHead title="Core Web Vitals" sub={`Real-world page experience from Google PageSpeed · mobile perf ${ps.mobile_score != null ? ps.mobile_score : ps.performance_score}/100`}
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, padding: 20 }}>
          {rows.map((m) => (
            <div key={m.k} style={{ border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 13, color: 'var(--sg-text-2)' }}>{m.k}</span>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: m.g.c, background: m.g.bg, padding: '3px 9px', borderRadius: 'var(--sg-radius-pill)' }}>{m.g.s}</span>
              </div>
              <div className="sg-tnum" style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 30, color: 'var(--sg-text)', margin: '10px 0 4px' }}>{m.v}</div>
              <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)' }}>{m.name}</div>
            </div>
          ))}
        </div>
        {/* Google PageSpeed opportunities + AI-agent readiness */}
        {(Array.isArray(ps.opportunities) && ps.opportunities.length > 0) || ps.agentic_score != null ? (
          <div style={{ borderTop: '1px solid var(--sg-border)', padding: '14px 20px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sg-text-2)' }}>Speed opportunities</span>
              {ps.agentic_score != null && (
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--sg-violet-700)', background: 'var(--sg-violet-50)', padding: '2px 9px', borderRadius: 'var(--sg-radius-pill)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Icon name="sparkles" size={12} /> AI-agent readiness {ps.agentic_score}/100
                </span>
              )}
            </div>
            {(ps.opportunities || []).map((o, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '7px 0', borderTop: i ? '1px solid var(--sg-border)' : 'none' }}>
                <span style={{ fontSize: 13, color: 'var(--sg-text)' }}>{o.title}</span>
                <span style={{ fontSize: 12.5, color: 'var(--sg-text-3)', whiteSpace: 'nowrap' }}>{o.display}</span>
              </div>
            ))}
          </div>
        ) : null}
      </Card>
    );
  }

  /* AI Search Readiness — can ChatGPT/Claude/Perplexity/Google AI crawl + read this
     site? Real signals from the crawl (robots.txt AI-bot rules, llms.txt/agent-card,
     JS-render). No traffic estimates. */
  function AiReadiness({ ar }) {
    if (!ar) return null;
    const items = [
      { ok: ar.robots_blocked.length === 0, critical: true,
        label: ar.robots_blocked.length === 0 ? 'robots.txt lets AI crawlers in' : 'robots.txt blocks ' + ar.robots_blocked.join(', ') },
      { ok: !ar.js_dependent, critical: true,
        label: ar.js_dependent ? 'Only ' + ar.word_count + ' words of static HTML — AI crawlers (no JS) may see an empty page' : 'Content is readable without JavaScript' },
      { ok: ar.llms_txt, critical: false,
        label: ar.llms_txt ? 'llms.txt present' : 'No llms.txt — add one to guide AI models' },
      { ok: ar.agent_card, critical: false,
        label: ar.agent_card ? 'Agent card present' : 'No agent-card.json' },
    ];
    const col = ar.score >= 80 ? 'var(--sg-green)' : ar.score >= 50 ? 'var(--sg-amber)' : 'var(--sg-red)';
    return (
      <Card>
        <CardHead title="AI Search Readiness" sub="Can ChatGPT, Claude, Perplexity & Google AI crawl and read this site?" icon="sparkles" />
        <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap', padding: '6px 20px 20px' }}>
          <div style={{ textAlign: 'center' }}>
            <Gauge value={ar.score} color={col} size={112} suffix="/100" />
          </div>
          <div style={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 11 }}>
            {items.map((it, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: 'var(--sg-text)' }}>
                <Icon name={it.ok ? 'check' : 'x'} size={17} strokeWidth={2.6} color={it.ok ? 'var(--sg-green)' : (it.critical ? 'var(--sg-red)' : 'var(--sg-amber)')} style={{ flex: 'none', marginTop: 1 }} />
                <span>{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  /* Internal linking — find places where a phrase ALREADY in one page's copy
     should link to another of the client's pages. On-demand (crawls several
     pages), cached server-side. Pure white-hat: own pages only. */
  function InternalLinks({ domain }) {
    const [data, setData] = React.useState(null);
    const [busy, setBusy] = React.useState(false);
    const [copied, setCopied] = React.useState(null);
    React.useEffect(() => { setData(null); setBusy(false); }, [domain]);
    const run = () => {
      if (busy) return;
      setBusy(true);
      window.SGLive.fetchInternalLinks().then((d) => { setBusy(false); setData(d || { error: true }); });
    };
    const copy = (t, i) => { try { navigator.clipboard.writeText(t); setCopied(i); setTimeout(() => setCopied(null), 1600); } catch (e) { /* noop */ } };
    const ops = (data && data.opportunities) || [];
    const orphans = (data && data.orphans) || [];
    return (
      <Card>
        <CardHead title="Internal linking" icon="link"
          sub="Places where a phrase already in your copy should link to another of your pages — spreads ranking power and helps AI crawlers follow your site."
          right={<button onClick={run} disabled={busy} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 14px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 12.5, color: '#fff', background: 'var(--sg-grad-brand)' }}>
            <Icon name="link" size={14} /> {busy ? 'Scanning pages…' : (data ? 'Re-scan' : 'Find opportunities')}
          </button>} />
        <div style={{ padding: 18 }}>
          {!data && !busy && <div style={{ fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>Scan your pages to find internal links you’re missing. We only suggest links between <strong>your own pages</strong> — no link schemes, no risk.</div>}
          {busy && <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>Crawling your pages and matching phrases…</div>}
          {data && data.error && <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>Couldn’t analyze this site right now — try again in a moment.</div>}
          {data && !data.error && (
            <React.Fragment>
              <div style={{ fontSize: 12.5, color: 'var(--sg-text-3)', marginBottom: 12 }}>{data.pages_scanned} page{data.pages_scanned === 1 ? '' : 's'} scanned · {ops.length} opportunit{ops.length === 1 ? 'y' : 'ies'} found{orphans.length ? ` · ${orphans.length} orphan page${orphans.length === 1 ? '' : 's'}` : ''}</div>
              {ops.length === 0
                ? <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>No missing internal links found — your pages already link to each other well.</div>
                : (
                  <div style={{ border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', overflow: 'hidden' }}>
                    {ops.map((o, i) => (
                      <div key={i} style={{ padding: '12px 14px', borderTop: i ? '1px solid var(--sg-border)' : 'none' }}>
                        <div style={{ fontSize: 13, color: 'var(--sg-text)', lineHeight: 1.5 }}>
                          On <strong>{o.from_title || o.from_url}</strong>, link the words <span style={{ fontWeight: 700, color: 'var(--sg-blue-700)', background: 'var(--sg-blue-50)', padding: '1px 6px', borderRadius: 5 }}>{o.anchor}</span> → <strong>{o.to_title || o.to_url}</strong>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--sg-text-3)', marginTop: 5, fontStyle: 'italic' }}>“…{o.context}…”</div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 7 }}>
                          <a href={o.from_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5, color: 'var(--sg-blue-600)', textDecoration: 'none' }}>Open page ↗</a>
                          <button onClick={() => copy('<a href="' + o.to_url + '">' + o.anchor + '</a>', i)} style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--sg-text-2)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{copied === i ? '✓ Copied' : 'Copy link HTML'}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              {orphans.length > 0 && (
                <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-amber-50)', border: '1px solid var(--sg-amber-100, #FBE5C8)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sg-text)', marginBottom: 5 }}>Orphan pages — nothing links to these</div>
                  {orphans.map((o, i) => <div key={i} style={{ fontSize: 12.5, color: 'var(--sg-text-2)' }}>{o.title || o.url}</div>)}
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </Card>
    );
  }

  const IMPACT = {
    high: { c: 'var(--sg-red)', bg: 'var(--sg-red-50)' },
    medium: { c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)' },
    low: { c: 'var(--sg-text-3)', bg: 'var(--sg-sunken)' },
  };
  function IssueRow({ it, c, last }) {
    const [open, setOpen] = React.useState(false);
    const imp = IMPACT[it.impact] || IMPACT.low;
    return (
      <div style={{ borderBottom: last ? 'none' : '1px solid var(--sg-border)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 120px', alignItems: 'center', gap: 14, padding: '13px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: c, flex: 'none' }} />
            {/* plainName first — "Largest Contentful Paint (LCP)" means nothing
                to the person paying for this. Falls back to the technical name
                for any check we have not written plain copy for yet. */}
            <span style={{ fontSize: 13.5, color: 'var(--sg-text)' }}>{it.plainName || it.name}</span>
          </div>
          <span style={{ justifySelf: 'start', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', color: imp.c, background: imp.bg, padding: '3px 9px', borderRadius: 'var(--sg-radius-pill)', textTransform: 'uppercase' }}>{it.impact}</span>
          <button onClick={() => setOpen((o) => !o)} style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 700, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer' }}>
            How to fix <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} />
          </button>
        </div>
        {open && (
          <div style={{ padding: '0 20px 16px 37px', animation: 'sg-fade 0.2s var(--sg-ease)' }}>
            <div style={{ display: 'flex', gap: 10, fontSize: 12.5, color: 'var(--sg-text-2)', lineHeight: 1.55, background: 'var(--sg-sunken)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', padding: '12px 14px' }}>
              <Icon name="help-circle" size={16} color="var(--sg-blue-600)" style={{ flex: 'none', marginTop: 1 }} />
              <span>{it.recommendation || it.details}</span>
            </div>
          </div>
        )}
      </div>
    );
  }
  function IssuesTable({ checks }) {
    const fails = checks.filter((c) => c.status === 'fail');
    const warns = checks.filter((c) => c.status === 'warning');
    const groups = [
      { sev: 'Error', c: 'var(--sg-red)', bg: 'var(--sg-red-50)', items: fails },
      { sev: 'Warning', c: 'var(--sg-amber)', bg: 'var(--sg-amber-50)', items: warns },
    ].filter((g) => g.items.length);
    return (
      <Card>
        <CardHead title="Issues to fix" sub="Real findings from the crawl — grouped by severity, start at the top"
          right={<span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--sg-green)', background: 'var(--sg-green-50)', padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>● LIVE</span>} />
        {groups.length === 0
          ? <div style={{ padding: 24, color: 'var(--sg-text-2)', fontSize: 13 }}>No errors or warnings — nice. 🎉</div>
          : groups.map((g) => (
            <div key={g.sev}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 20px', background: 'var(--sg-sunken)', borderBottom: '1px solid var(--sg-border)' }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: g.c, background: g.bg, padding: '3px 10px', borderRadius: 'var(--sg-radius-pill)' }}>{g.sev}</span>
                <span className="sg-tnum" style={{ fontSize: 12, color: 'var(--sg-text-3)' }}>{g.items.length} {g.items.length === 1 ? 'issue' : 'issues'}</span>
              </div>
              {g.items.map((it, i) => <IssueRow key={it.id} it={it} c={g.c} last={i === g.items.length - 1} />)}
            </div>
          ))}
      </Card>
    );
  }

  /* Keywords to target — closes out a scan with AI keyword ideas matched to the
     business/locale, add-to-tracking inline. Respects the plan's keyword cap. */
  function KeywordTargets({ domain, perf }) {
    // Real Google Search Console queries for this domain — proven demand.
    const gscQueries = React.useMemo(() => {
      const rows = (perf && perf.byQuery) || [];
      const key = (r) => (r.keys && r.keys[0]) || r.query || '';
      const near = rows.filter((r) => r.position >= 8 && r.position <= 30).slice().sort((a, b) => (b.impressions || 0) - (a.impressions || 0)).map(key);
      const top = rows.slice(0, 12).map(key);
      return Array.from(new Set([...near, ...top].filter(Boolean))).slice(0, 20);
    }, [perf]);

    const [approved, setApproved] = React.useState([]);   // already-tracked → excluded
    const [candidates, setCandidates] = React.useState(null); // /suggest: search + competitor + site, always up to 5
    const [sel, setSel] = React.useState([]);
    const [approving, setApproving] = React.useState(false);
    const [msg, setMsg] = React.useState(null);
    // On-demand "find similar" (real DataForSEO) — ONLY runs when the user types + clicks.
    const [seedInput, setSeedInput] = React.useState('');
    const [activeSeeds, setActiveSeeds] = React.useState([]);
    const [similar, setSimilar] = React.useState(null);    // null=not searched; []=none
    const [simBusy, setSimBusy] = React.useState(false);
    const [simMeta, setSimMeta] = React.useState(null);

    const reloadApproved = () => window.SGLive.fetchApprovedKeywords().then((a) => setApproved((a || []).map((x) => (typeof x === 'string' ? x : (x && x.keyword))).filter(Boolean)));
    // Default suggestions = REAL keywords only, always up to 5: Search Console
    // queries + competitor keyword-gap + the site's content/meta. No AI invention.
    const loadCandidates = React.useCallback(() => {
      setCandidates(null);
      window.SGLive.fetchSuggestedKeywords(8, gscQueries).then((rows) => setCandidates(rows || []));
    }, [gscQueries]);
    React.useEffect(() => {
      setSel([]); setMsg(null); setSeedInput(''); setActiveSeeds([]); setSimilar(null); setSimMeta(null);
      reloadApproved(); loadCandidates();
      /* eslint-disable-next-line */
    }, [domain, loadCandidates]);

    const approvedSet = React.useMemo(() => new Set(approved.map((k) => k.toLowerCase())), [approved]);

    const findSimilar = () => {
      const seeds = seedInput.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 12);
      const seed = seeds[0];
      if (!seed || simBusy) return;
      setSimBusy(true); setActiveSeeds(seeds); setSimilar(null); setSel([]); setMsg(null); setSimMeta(null);
      // Your own "<phrase> in <place>" patterns applied to the other places you
      // target, plus real related searches — never AI-invented junk.
      window.SGLive.fetchSimilarKeywords(seed, 12).then((r) => {
        setSimBusy(false);
        if (!r.ok) { setSimMeta({ error: r.error || 'Could not load suggestions right now.' }); setSimilar([]); return; }
        const rows = (r.data || [])
          .map((x) => ({ keyword: (x.keyword || '').trim().toLowerCase(), source: x.source, volume: x.volume }))
          .filter((x) => x.keyword && !approvedSet.has(x.keyword));
        setSimilar(rows);
      });
    };
    const clearSimilar = () => { setSeedInput(''); setActiveSeeds([]); setSimilar(null); setSel([]); };

    const showingSimilar = activeSeeds.length > 0;
    const list = showingSimilar ? (similar || []) : (candidates || []);
    const loading = showingSimilar ? simBusy : (candidates === null);

    const toggle = (k) => setSel((s) => (s.indexOf(k) >= 0 ? s.filter((x) => x !== k) : s.concat(k)));
    const approve = () => {
      if (!sel.length || approving) return;
      setApproving(true); setMsg(null);
      window.SGLive.approveKeywords(sel).then((r) => {
        setApproving(false);
        if (r && r.ok) {
          setMsg({ ok: !r.skipped, text: r.skipped
            ? `Added ${r.added} of ${r.requested}. ${r.skipped} skipped — you're at your plan's limit of ${r.limit} tracked keywords (${r.used} used across all your domains). Remove some in the Keywords tab, or upgrade.`
            : `Added ${r.added} to your keywords — checking their rankings now.` });
          setSel([]); reloadApproved(); loadCandidates(); // refresh, now excluding the approved
        } else setMsg({ ok: false, text: (r && r.message) || 'Could not add those keywords.' });
      });
    };

    const SRC = { search: ['Search query', 'var(--sg-blue-700)', 'var(--sg-blue-50)'], competitor: ['Competitor', 'var(--sg-rust, #C2410C)', 'var(--sg-amber-50)'], related: ['Related', 'var(--sg-violet-700)', 'var(--sg-violet-50)'], meta: ['Meta tag', 'var(--sg-violet-700)', 'var(--sg-violet-50)'], website: ['Website', 'var(--sg-text-2)', 'var(--sg-sunken)'] };
    const rowBase = { display: 'flex', alignItems: 'center', gap: 11, padding: '9px 12px', cursor: 'pointer' };

    return (
      <Card>
        <CardHead ai title="Keywords to target"
          sub={showingSimilar ? `Ideas similar to: ${activeSeeds.join(', ')}` : `Real keywords for ${domain || 'this site'} — from your Search Console, your competitors' rankings and your website content. Tick the ones to track, or type a keyword below to find similar ideas.`} />
        <div style={{ padding: 18 }}>
          {/* Type a keyword → find similar (AI). Only runs on explicit click. */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <input value={seedInput} onChange={(e) => setSeedInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') findSimilar(); }}
              placeholder="Type a keyword to find similar ones…"
              style={{ flex: 1, minWidth: 220, height: 40, padding: '0 14px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13, color: 'var(--sg-text)', outline: 'none' }} />
            <button onClick={findSimilar} disabled={simBusy || !seedInput.trim()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: (simBusy || !seedInput.trim()) ? 'not-allowed' : 'pointer', opacity: (simBusy || !seedInput.trim()) ? 0.55 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-violet)' }}>
              <Icon name="sparkles" size={15} /> {simBusy ? 'Finding…' : 'Find similar'}
            </button>
            {showingSimilar && <button onClick={clearSimilar} style={{ height: 40, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', color: 'var(--sg-text-2)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>← Back to your keywords</button>}
          </div>

          {simMeta && simMeta.error
            ? <div style={{ fontSize: 13, color: 'var(--sg-text-2)', lineHeight: 1.55 }}>{simMeta.error}</div>
            : loading
              ? <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{showingSimilar ? 'Finding similar keywords…' : 'Reading your website & search queries…'}</div>
              : list.length === 0
                ? <div style={{ fontSize: 13, color: 'var(--sg-text-2)' }}>{showingSimilar ? 'No similar ideas found — try a different keyword.' : 'No keywords found from your website or Search Console yet. Type one above to find ideas, or add keywords in the Keywords tab.'}</div>
                : (
                  <div style={{ border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', overflow: 'hidden' }}>
                    {list.map((it, i) => {
                      const src = !showingSimilar && SRC[it.source];
                      return (
                        <label key={it.keyword} style={{ ...rowBase, borderTop: i ? '1px solid var(--sg-border)' : 'none', background: sel.indexOf(it.keyword) >= 0 ? 'var(--sg-blue-50)' : 'transparent' }}>
                          <input type="checkbox" checked={sel.indexOf(it.keyword) >= 0} onChange={() => toggle(it.keyword)} style={{ width: 16, height: 16, accentColor: 'var(--sg-blue-600)', cursor: 'pointer', flex: 'none' }} />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 13.5, color: 'var(--sg-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.keyword}</span>
                            {it.why && <span style={{ display: 'block', fontSize: 11, color: 'var(--sg-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.why}</span>}
                          </span>
                          {src && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: src[1], background: src[2], padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)', flex: 'none' }}>{src[0]}</span>}
                          {showingSimilar && it.volume != null && it.volume > 0 && <span className="sg-tnum" style={{ fontSize: 11.5, color: 'var(--sg-text-3)', flex: 'none' }}>{it.volume.toLocaleString()}/mo</span>}
                          {showingSimilar && <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: it.source === 'pattern' ? 'var(--sg-blue-700)' : 'var(--sg-violet-700)', background: 'var(--sg-card)', border: '1px solid ' + (it.source === 'pattern' ? 'var(--sg-blue-100, #D6E4FF)' : 'var(--sg-violet-100)'), padding: '2px 7px', borderRadius: 'var(--sg-radius-pill)', flex: 'none' }}>{it.source === 'pattern' ? 'Your pattern' : 'Related'}</span>}
                        </label>
                      );
                    })}
                  </div>
                )}

          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={approve} disabled={!sel.length || approving}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 42, padding: '0 18px', border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: (!sel.length || approving) ? 'not-allowed' : 'pointer', opacity: (!sel.length || approving) ? 0.55 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: '#fff', background: 'var(--sg-grad-brand)' }}>
              <Icon name="check" size={16} /> {approving ? 'Adding…' : sel.length ? `Add ${sel.length} to my keywords` : 'Select keywords to add'}
            </button>
            {list.length > 0 && <button onClick={() => setSel(list.map((x) => x.keyword))} style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--sg-blue-600)', background: 'none', border: 'none', cursor: 'pointer' }}>Select all</button>}
          </div>
          {msg && <div style={{ marginTop: 10, fontSize: 12.5, color: msg.ok ? 'var(--sg-green)' : 'var(--sg-red, #DC2626)' }}>{msg.text}</div>}
        </div>
      </Card>
    );
  }

  function SiteAudit() {
    const { site, prettySite, perf } = window.SGLive.useLive();
    const dom = site ? prettySite(site) : '';
    const [audit, setAudit] = React.useState(null);
    const [loaded, setLoaded] = React.useState(false);
    const [scanning, setScanning] = React.useState(false);

    // Load the latest audit FOR THE SELECTED domain; reset when the domain changes.
    React.useEffect(() => {
      if (!dom) return undefined;
      setAudit(null); setLoaded(false);
      window.SGLive.fetchLatestAudit(dom).then((a) => { setAudit(a); setLoaded(true); });
      return undefined;
    }, [dom]);

    const runAudit = async () => {
      if (!dom) return;
      setScanning(true);
      try { await window.SGLive.runSeoAudit(dom); const a = await window.SGLive.fetchLatestAudit(dom); setAudit(a); }
      finally { setScanning(false); }
    };
    const g = { scanningSeo: scanning };
    const scanned = !!audit;
    // Send-this-audit-to-client (reuses the live report email stack).
    const [sendOpen, setSendOpen] = React.useState(false);
    const [rcpt, setRcpt] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [toast, setToast] = React.useState(null);
    const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 3200); };
    const doSend = () => {
      const to = (rcpt || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) { flash('Enter a valid recipient email.'); return; }
      if (sending) return;
      setSending(true);
      window.SGLive.sendReport({ to, template: 'full', domain: dom }).then((r) => {
        setSending(false);
        if (r && r.success) { setSendOpen(false); setRcpt(''); flash('Audit report emailed to ' + to + '.'); }
        else flash((r && r.error) || 'Could not send the report.');
      });
    };

    if (!scanned) {
      return (
        <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <PageHead eyebrow="Research" title="Site Audit" icon="activity" sub={dom ? `Crawl ${dom} for the technical SEO issues that hold back rankings.` : 'Crawl your site for the technical SEO issues that hold back rankings.'} />
          {!loaded && !scanning ? (
            <Card style={{ padding: 22, fontSize: 13, color: 'var(--sg-text-2)' }}>Loading the latest audit for {dom || 'this domain'}…</Card>
          ) : (
          <Card style={{ padding: '64px 24px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <span style={{ width: 64, height: 64, borderRadius: 'var(--sg-radius-xl)', background: 'var(--sg-blue-50)', color: 'var(--sg-blue-600)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="activity" size={30} /></span>
            <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 600, fontSize: 22, color: 'var(--sg-text)' }}>Audit {dom || 'your site'}</div>
            <div style={{ fontSize: 14, color: 'var(--sg-text-2)', maxWidth: 460, lineHeight: 1.55 }}>We’ll crawl the homepage and check titles, meta, headings, schema, links and more — then score it and list the fixes. Takes a few seconds.</div>
            <button onClick={runAudit} disabled={g.scanningSeo}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 24px', marginTop: 6, border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: g.scanningSeo ? 'wait' : 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 14.5, color: '#fff', background: 'var(--sg-grad-brand)', boxShadow: '0 4px 16px rgba(37,99,235,0.32)' }}>
              <Icon name="activity" size={17} /> {g.scanningSeo ? 'Crawling your site…' : 'Run site audit'}
            </button>
          </Card>
          )}
        </div>
      );
    }

    const score = audit ? audit.score : 0;
    return (
      <div style={{ maxWidth: 1180, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHead eyebrow="Research" title="Site Audit" icon="activity"
          sub={audit ? `${audit.url || ''} · ${audit.summary ? audit.summary.total : 0} checks run.` : 'Latest crawl.'}
          right={(
            <React.Fragment>
              <div style={{ position: 'relative' }}>
                <button onClick={() => setSendOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 42, padding: '0 16px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 600, fontSize: 13.5, color: 'var(--sg-text)' }}>
                  <Icon name="file-text" size={15} color="var(--sg-text-2)" /> Send report to client
                </button>
                {sendOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 40, width: 300, background: 'var(--sg-card)', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-lg)', boxShadow: 'var(--sg-shadow-lg)', padding: 16 }}>
                    <div style={{ fontFamily: 'var(--sg-font-display)', fontWeight: 700, fontSize: 14.5, color: 'var(--sg-text)', marginBottom: 4 }}>Email this audit</div>
                    <div style={{ fontSize: 12.5, color: 'var(--sg-text-2)', marginBottom: 10, lineHeight: 1.5 }}>Sends the full SEO + GEO report for {dom || 'this site'} to your client’s inbox.</div>
                    <input type="email" value={rcpt} onChange={(e) => setRcpt(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doSend(); }} placeholder="client@email.com" autoFocus style={{ width: '100%', height: 40, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-md)', background: 'var(--sg-card)', fontFamily: 'var(--sg-font-sans)', fontSize: 13, color: 'var(--sg-text)', outline: 'none', boxSizing: 'border-box' }} />
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button onClick={doSend} disabled={sending} style={{ flex: 1, height: 38, border: 'none', borderRadius: 'var(--sg-radius-pill)', cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1, fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13, color: '#fff', background: 'var(--sg-grad-brand)' }}>{sending ? 'Sending…' : 'Send now'}</button>
                      <button onClick={() => setSendOpen(false)} style={{ height: 38, padding: '0 12px', border: '1px solid var(--sg-border)', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-card)', cursor: 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 600, fontSize: 13, color: 'var(--sg-text-2)' }}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <button onClick={runAudit} disabled={g.scanningSeo} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 42, padding: '0 16px', border: 'none', borderRadius: 'var(--sg-radius-pill)', background: 'var(--sg-grad-brand)', cursor: g.scanningSeo ? 'wait' : 'pointer', fontFamily: 'var(--sg-font-sans)', fontWeight: 700, fontSize: 13.5, color: '#fff' }}>
                <Icon name="refresh-cw" size={15} color="#fff" /> {g.scanningSeo ? 'Crawling…' : 'Re-crawl'}
              </button>
            </React.Fragment>
          )} />
        {toast && (
          <div style={{ position: 'fixed', top: 78, left: '50%', transform: 'translateX(-50%)', zIndex: 71, maxWidth: 460, background: 'var(--sg-text)', color: '#fff', fontSize: 13, lineHeight: 1.5, padding: '11px 16px', borderRadius: 'var(--sg-radius-md)', boxShadow: 'var(--sg-shadow-lg)' }}>{toast}</div>
        )}
        <HealthHero score={score} summary={audit ? audit.summary : null} />
        <CoreVitals ps={audit ? audit.pagespeed : null} />
        <AiReadiness ar={audit ? audit.ai_readiness : null} />
        <InternalLinks domain={dom} />
        <IssuesTable checks={audit ? audit.checks : []} />
        <KeywordTargets domain={dom} perf={perf} />
      </div>
    );
  }

  window.SGSiteAudit = SiteAudit;
})();
